import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, Optional, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import {
  Analytics,
  logEvent as firebaseLogEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
  setUserProperties,
} from '@angular/fire/analytics';
import { CustomParams } from 'firebase/analytics';
import { filter } from 'rxjs/operators';

/**
 * Firebase / GA4 analytics for the web app. No-ops on the server and when Analytics is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  constructor(@Optional() private readonly analytics: Analytics | null) {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.logPageView());
  }

  setCollectionEnabled(enabled: boolean): void {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    setAnalyticsCollectionEnabled(this.analytics, enabled);
  }

  setUser(userId: string | null): void {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    setUserId(this.analytics, userId);
  }

  setUserProps(props: { [key: string]: string }): void {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    setUserProperties(this.analytics, props as CustomParams);
  }

  /** Call on sign-out so the GA4 client does not keep the previous user's identity. */
  clearUserIdentity(): void {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    this.setUser(null);
    setUserProperties(this.analytics, {
      user_role: 'anonymous',
      email_verified: 'false',
    } as CustomParams);
  }

  logEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
    if (!isPlatformBrowser(this.platformId) || !this.analytics) {
      return;
    }
    firebaseLogEvent(this.analytics, eventName, params as CustomParams);
  }

  private logPageView(): void {
    if (!this.analytics) {
      return;
    }
    const page_path = this.router.url.split('?')[0] || '/';
    const page_title = this.title.getTitle();
    const page_location =
      typeof location !== 'undefined' ? `${location.origin}${this.router.url}` : undefined;

    firebaseLogEvent(this.analytics, 'page_view', {
      page_path,
      page_title,
      ...(page_location ? { page_location } : {}),
    });
  }
}
